import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardEdit, Loader2, CheckCircle2, Pencil, Check, X, User } from "lucide-react";
import KpiCaptureCard from "@/components/ops/KpiCaptureCard";

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Registro ya capturado — permite corregir el valor (solo RH/corp_admin)
function CapturedRecordCard({ item, onSaved }) {
  const rec = item.existing_record;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rec?.value ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const num = parseFloat(draft);
    if (Number.isNaN(num)) {
      toast.error("Ingresa un valor numérico");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/ops/kpis/records/${rec.id}`, { value: num });
      toast.success("Registro corregido");
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo corregir el registro");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-green-200 p-4 sm:p-5" data-testid={`captura-done-${item.kpi_definition_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">Capturado</span>
            <span className="text-[11px] text-slate-400">{item.capture_period}</span>
          </div>
          <div className="font-bold text-slate-900 text-base">{item.name}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            Capturado por {rec?.created_by_name || "—"} · meta {item.target_value ?? item.target} {item.unit}
          </div>
        </div>
        <div className="text-right shrink-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="any"
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="w-24 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
                data-testid={`captura-edit-input-${item.kpi_definition_id}`}
              />
              <button onClick={save} disabled={saving} className="text-green-600" data-testid={`captura-edit-save-${item.kpi_definition_id}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              </button>
              <button onClick={() => setEditing(false)} className="text-slate-400"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button
              onClick={() => { setDraft(rec?.value ?? ""); setEditing(true); }}
              className="group inline-flex items-center gap-1.5"
              title="Corregir valor"
              data-testid={`captura-edit-btn-${item.kpi_definition_id}`}
            >
              <span className="text-xl font-extrabold text-slate-800">{rec?.value}</span>
              <span className="text-xs text-slate-400">{item.unit}</span>
              <Pencil className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OpsKpiCapturaRH() {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [categoryCode, setCategoryCode] = useState("");
  const [branchId, setBranchId] = useState("");
  const [period, setPeriod] = useState(currentPeriod());
  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);

  const selectedCat = categories.find((c) => c.code === categoryCode);
  const needsBranch = selectedCat?.scope === "por_sucursal";

  useEffect(() => {
    api.get("/ops/kpis/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    api.get("/branches").then((r) => setBranches((r.data || []).filter((b) => !b.is_corporate))).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!categoryCode || (needsBranch && !branchId)) {
      setGrid(null);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ category_code: categoryCode, period });
      if (needsBranch) params.set("branch_id", branchId);
      const res = await api.get(`/ops/kpis/capture-grid?${params}`);
      setGrid(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar la grilla de captura");
      setGrid(null);
    } finally {
      setLoading(false);
    }
  }, [categoryCode, branchId, period, needsBranch]);

  useEffect(() => { load(); }, [load]);

  const pendingItems = (grid?.items || []).filter((i) => !i.existing_record);
  const capturedItems = (grid?.items || []).filter((i) => i.existing_record);

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto space-y-6" data-testid="captura-rh-page">
      <div>
        <div className="flex items-center gap-2">
          <ClipboardEdit className="w-6 h-6 text-[#1A1A1A]" />
          <h1 className="text-2xl font-bold text-slate-900">Captura RH de KPIs</h1>
        </div>
        <p className="text-sm text-slate-500 mt-0.5">
          Los resultados manuales los captura únicamente RH — nunca la persona evaluada.
        </p>
      </div>

      {/* Selectores */}
      <div className="bg-slate-50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Puesto</label>
          <select
            value={categoryCode}
            onChange={(e) => { setCategoryCode(e.target.value); setBranchId(""); }}
            className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm"
            data-testid="captura-puesto-select"
          >
            <option value="">Selecciona un puesto…</option>
            {categories.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Sucursal</label>
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            disabled={!needsBranch}
            className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm disabled:opacity-50"
            data-testid="captura-sucursal-select"
          >
            <option value="">{needsBranch ? "Selecciona sucursal…" : "No aplica (corporativo)"}</option>
            {needsBranch && branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Período</label>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm"
            data-testid="captura-period-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="bg-white rounded-xl border border-slate-200 h-32 animate-pulse" />)}
        </div>
      ) : !grid ? (
        <div className="text-center py-16 text-sm text-slate-400" data-testid="captura-empty-hint">
          Selecciona un puesto {needsBranch ? "y una sucursal " : ""}para capturar sus KPIs manuales.
        </div>
      ) : (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-sm">
            <div className="font-bold text-slate-800">{grid.category_name}{grid.branch_name ? ` · ${grid.branch_name}` : ""}</div>
            {grid.objective && <p className="text-slate-500 text-xs mt-1">{grid.objective}</p>}
            <p className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1">
              <User className="w-3 h-3" /> Responsable: {grid.responsible?.name || "Sin asignar"}
            </p>
          </div>

          {pendingItems.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Pendientes de captura ({pendingItems.length})</h2>
              {pendingItems.map((item) => (
                <KpiCaptureCard
                  key={item.kpi_definition_id}
                  kpi={item}
                  onSaved={load}
                  userRole={user?.role}
                />
              ))}
            </div>
          )}

          {capturedItems.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">Ya capturados ({capturedItems.length})</h2>
              {capturedItems.map((item) => (
                <CapturedRecordCard key={item.kpi_definition_id} item={item} onSaved={load} />
              ))}
            </div>
          )}

          {grid.items.length === 0 && (
            <div className="text-center py-12 text-sm text-slate-400" data-testid="captura-no-manual">
              Este puesto no tiene KPIs de captura manual (todos son automáticos).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
